import { type SelectHTMLAttributes, forwardRef } from 'react';
import { cn } from '@/lib/cn';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  /** Optional accessible label rendered above the field. */
  label?: string;
  /** Inline validation / helper message under the field. */
  error?: string;
  options: SelectOption[];
  /** Placeholder option shown when value is empty. */
  placeholder?: string;
}

/**
 * Native select matching Figma Input tokens: 44px surface field, default border.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { className, label, error, id, disabled, options, placeholder, value, ...props },
  ref,
) {
  const fieldId = id ?? props.name;
  const showPlaceholder = Boolean(placeholder) && (value === '' || value === undefined);

  return (
    <div className={cn('flex w-full flex-col gap-1', className)}>
      {label ? (
        <label htmlFor={fieldId} className="text-label-s text-text-secondary">
          {label}
        </label>
      ) : null}
      <select
        ref={ref}
        id={fieldId}
        disabled={disabled}
        value={value}
        aria-invalid={error ? true : undefined}
        aria-describedby={error && fieldId ? `${fieldId}-error` : undefined}
        className={cn(
          'h-11 w-full appearance-none rounded-md border border-border-default bg-bg-surface px-4 py-3',
          'text-body-l text-text-primary',
          'outline-none transition-colors focus-visible:border-border-focus',
          'disabled:cursor-not-allowed disabled:opacity-60',
          showPlaceholder && 'text-text-secondary',
          error && 'border-status-danger',
        )}
        {...props}
      >
        {placeholder ? (
          <option value="" disabled>
            {placeholder}
          </option>
        ) : null}
        {options.map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
      </select>
      {error ? (
        <p id={fieldId ? `${fieldId}-error` : undefined} className="text-caption text-status-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
});
